/**
 * C5 §6 — the full-screen connectivity takeover.
 *
 * Three flavors (offline / maintenance / outage) share ONE skeleton, differing
 * only in copy. Mounted app-wide (main.tsx, beside GlobalFailureScreen) as a
 * fixed overlay: it renders nothing while online or during the silent grace
 * window, covers the app when a connectivity problem is committed, and tears
 * itself down automatically when the controller recovers (which also resumes
 * react-query via the registered recovery hooks).
 *
 * House style (inherited from the legacy "We're on it." error pages,
 * modernized): text #333, brand orange #dc8100, generous centered whitespace,
 * a warm/brief voice signed "— The Ving Team", a support mailto, and an "if it
 * lasts more than a minute…" reassurance.
 */
import type { ReactNode } from 'react';
import { ErrorScreenLayout } from '@/components/errors/ErrorScreenLayout';
import { vars } from '@/theme';
import { useConnectivity } from './useConnectivity';
import { triggerRecoveryProbe, type ConnectivityStatus } from './connectivityController';

const TEXT = '#333';
const BRAND_ORANGE = '#dc8100';
const SUPPORT_EMAIL = 'support@vingapp.com';

interface ScreenCopy {
  testId: string;
  icon: string;
  title: string;
  body: string;
  /** W3.5: progress reassurance — shown on offline and outage screens. */
  progressNote?: string;
}

// offline / outage / maintenance — the only three committed, rendered states.
const COPY: Record<'offline' | 'outage' | 'maintenance', ScreenCopy> = {
  offline: {
    testId: 'connectivity-offline',
    icon: '⚡',
    title: 'You appear to be offline',
    body: "We can't reach the network right now. We'll reconnect you automatically the moment your connection is back.",
    progressNote: 'Your progress on this page is kept and will be saved when we reconnect — keep this tab open.',
  },
  outage: {
    testId: 'connectivity-outage',
    icon: '!',
    title: "We're on it",
    body: "We're experiencing some server issues. We expect to be back shortly — no need to do anything, this page will pick right back up on its own.",
    progressNote: 'Your progress on this page is kept and will be saved when we reconnect — keep this tab open.',
  },
  maintenance: {
    testId: 'connectivity-maintenance',
    icon: '⚙',
    title: 'Down for a little maintenance',
    body: "Ving is briefly down for scheduled maintenance. We'll be back shortly — this page will reconnect on its own.",
  },
};

function isCommitted(status: ConnectivityStatus): status is 'offline' | 'outage' | 'maintenance' {
  return status === 'offline' || status === 'outage' || status === 'maintenance';
}

function heading(children: ReactNode) {
  return (
    <h1 style={{ color: TEXT, fontSize: 24, fontWeight: 600, margin: '0 0 16px', lineHeight: 1.4 }}>
      {children}
    </h1>
  );
}

export function ConnectivityBoundary() {
  const { status } = useConnectivity();
  if (!isCommitted(status)) return null;

  const copy = COPY[status];

  return (
    <div data-testid="connectivity-screen" data-flavor={status}>
      <ErrorScreenLayout icon={copy.icon} iconBgColor={BRAND_ORANGE} testId={copy.testId}>
        {heading(copy.title)}

        <p style={{ color: TEXT, fontSize: 15, lineHeight: 1.6, margin: '0 0 8px' }}>{copy.body}</p>

        {copy.progressNote && (
          <p style={{ color: TEXT, fontSize: 14, lineHeight: 1.6, margin: '0 0 8px', fontWeight: 600 }}>{copy.progressNote}</p>
        )}

        <p style={{ color: TEXT, fontSize: 14, lineHeight: 1.6, margin: '0 0 24px', opacity: 0.85 }}>
          If it lasts more than a minute, drop us a line at{' '}
          <a href={`mailto:${SUPPORT_EMAIL}`} style={{ color: BRAND_ORANGE, fontWeight: 600 }}>
            {SUPPORT_EMAIL}
          </a>
          .
        </p>

        <button
          type="button"
          data-testid="connectivity-retry"
          onClick={() => triggerRecoveryProbe()}
          style={{
            padding: '8px 20px',
            fontSize: 14,
            fontWeight: 600,
            color: vars.color.fixed.white,
            background: BRAND_ORANGE,
            border: 'none',
            borderRadius: 0,
            cursor: 'pointer',
            marginBottom: 24,
          }}
        >
          Try again
        </button>

        <p style={{ color: TEXT, fontSize: 13, opacity: 0.6, margin: 0 }}>— The Ving Team</p>
      </ErrorScreenLayout>
    </div>
  );
}
